import { Injectable } from '@angular/core';
import { Auth, signInWithPopup, GoogleAuthProvider, signOut, UserCredential } from '@angular/fire/auth';
import { from, Observable, switchMap, of, catchError } from 'rxjs';
import { CalmeanAuthService } from './calmean-auth.service';

@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
    constructor(
        private auth: Auth,
        private calmeanAuthService: CalmeanAuthService
    ) {}

    /**
     * Sign in with Google and then authenticate with Calmean backend
     */
    signInWithGoogle(): Observable<any> {
        return from(signInWithPopup(this.auth, new GoogleAuthProvider()))
            .pipe(
                switchMap((credential: UserCredential) => {
                    // Get the Google access token
                    const googleUser = credential.user;
                    const googleCredential = GoogleAuthProvider.credentialFromResult(credential);
                    const token = googleCredential?.accessToken;

                    if (!token) {
                        throw new Error('Failed to get Google access token');
                    }

                    // Store Firebase user info
                    const firebaseUser = {
                        uid: googleUser.uid,
                        email: googleUser.email,
                        displayName: googleUser.displayName,
                        photoURL: googleUser.photoURL
                    };

                    localStorage.setItem('firebaseUser', JSON.stringify(firebaseUser));

                    // Authenticate with Calmean backend
                    return this.calmeanAuthService.loginWithGoogle(googleUser.email!, token)
                        .pipe(
                            switchMap(account => {
                                // Combine Firebase and Calmean user data
                                const user = {
                                    ...firebaseUser,
                                    calmeanAccount: account
                                };

                                // Store the combined user object in localStorage
                                localStorage.setItem('firebaseUser', JSON.stringify(user));

                                console.log('Stored user with calmeanAccount:', user);
                                return of(user);
                            }),
                            catchError(error => {
                                // If Calmean auth fails, still return Firebase user
                                console.error('Calmean authentication failed:', error);
                                return of(firebaseUser);
                            })
                        );
                })
            );
    }

    /**
     * Sign out from both Firebase and clear Calmean tokens
     */
    logout(): Observable<void> {
        // Clear Calmean token
        localStorage.removeItem('calmeanToken');
        localStorage.removeItem('firebaseUser');

        // Sign out from Firebase
        return from(signOut(this.auth));
    }
}
